import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Leaf, MapPin, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useIsWaitlist, useReopeningWindow } from "@/context/ConfigContext";
import { Toaster } from "@/components/ui/sonner";

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const isWaitlist = useIsWaitlist();
  const reopening = useReopeningWindow();
  const window_ = t({ en: reopening.en, fr: reopening.fr });
  const nav_ = useNavigate();

  const nav = [
    { to: "/about", label: t({ en: "About", fr: "À propos" }) },
    { to: "/counselling", label: t({ en: "Counselling", fr: "Counseling" }) },
    { to: "/groups", label: t({ en: "Groups", fr: "Groupes" }) },
    { to: "/consulting", label: t({ en: "Consulting", fr: "Services-conseils" }) },
    { to: "/media", label: t({ en: "Media", fr: "Médias" }) },
    { to: "/contact", label: t({ en: "Contact", fr: "Contact" }) },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-primary)" }}>
      <div className="hidden md:block" style={{ background: "var(--brand)", color: "#FBF9F5" }} data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-4 py-2 text-xs flex items-center justify-between font-mono tracking-wider uppercase">
          <span className="flex items-center gap-2"><MapPin size={12} /> {t({ en: "Licensed MSW · Quebec & Ontario · 10+ Years in Practice", fr: "T.S. autorisée · Québec et Ontario · 10+ années de pratique" })}</span>
          <span>{isWaitlist ? t({ en: `Reopening ${window_} — join the waitlist`, fr: `Réouverture ${window_} — rejoignez la liste` }) : t({ en: "Currently accepting new clients", fr: "Nouveaux clients acceptés" })}</span>
        </div>
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ background: "rgba(251, 249, 245, 0.85)", borderColor: "var(--border-subtle)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
            <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--sage)" }}>
              <Leaf size={18} style={{ color: "var(--brand)" }} />
            </span>
            <div className="leading-tight">
              <div className="font-serif text-xl" style={{ color: "var(--ink)" }}>JD Drummond</div>
              <div className="overline text-[9px]" style={{ letterSpacing: "0.25em" }}>{t({ en: "Counselling & Consulting", fr: "Consultation et services-conseils" })}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-[var(--terracotta)]" : "text-[var(--ink-2)] hover:text-[var(--brand)]"}`} data-testid={`nav-${n.to.replace("/", "")}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button onClick={() => setLang(lang === "en" ? "fr" : "en")} className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-full border transition-colors" style={{ borderColor: "var(--border-medium)", color: "var(--brand)" }} data-testid="lang-toggle" aria-label="Toggle language">
              <Globe size={14} /> {lang === "en" ? "FR" : "EN"}
            </button>
            {user ? (
              <>
                {user.role === "admin"
                  ? <Link to="/admin/waitlist" className="text-sm font-medium text-[var(--brand)]" data-testid="nav-admin-waitlist">{t({ en: "Sign-ups", fr: "Inscriptions" })}</Link>
                  : <Link to="/portal" className="text-sm font-medium text-[var(--brand)]" data-testid="nav-portal">{t({ en: "Portal", fr: "Espace client" })}</Link>}
                <button onClick={() => { logout(); nav_("/"); }} className="text-sm text-[var(--ink-3)]" data-testid="btn-logout">{t({ en: "Sign out", fr: "Déconnexion" })}</button>
              </>
            ) : (
              <Link to="/login" className="text-sm font-medium text-[var(--ink-2)] hover:text-[var(--brand)]" data-testid="nav-login">{t({ en: "Sign in", fr: "Connexion" })}</Link>
            )}
            <Link to={isWaitlist ? "/counselling" : "/book"} className="btn-accent text-sm" data-testid="btn-book-header">{isWaitlist ? t({ en: "Join waitlist", fr: "Liste d'attente" }) : t({ en: "Book a session", fr: "Prendre rendez-vous" })}</Link>
          </div>

          <button className="lg:hidden flex items-center gap-2" onClick={() => setOpen(!open)} data-testid="btn-mobile-menu">
            <button onClick={(e) => { e.stopPropagation(); setLang(lang === "en" ? "fr" : "en"); }} className="text-xs font-mono uppercase px-2 py-1 rounded-full border" style={{ borderColor: "var(--border-medium)" }} data-testid="lang-toggle-mobile">{lang === "en" ? "FR" : "EN"}</button>
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t px-4 py-4 space-y-3" style={{ borderColor: "var(--border-subtle)" }} data-testid="mobile-menu">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="block py-2 text-[var(--ink-2)]">{n.label}</NavLink>
            ))}
            {user ? (
              <>
                {user.role === "admin"
                  ? <Link to="/admin/waitlist" onClick={() => setOpen(false)} className="block py-2 text-[var(--brand)] font-medium" data-testid="mobile-nav-admin-waitlist">{t({ en: "Sign-ups", fr: "Inscriptions" })}</Link>
                  : <Link to="/portal" onClick={() => setOpen(false)} className="block py-2 text-[var(--brand)] font-medium">{t({ en: "Client Portal", fr: "Espace client" })}</Link>}
                <button onClick={() => { logout(); setOpen(false); nav_("/"); }} className="block py-2 text-[var(--ink-3)]">{t({ en: "Sign out", fr: "Déconnexion" })}</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="block py-2 text-[var(--ink-2)]">{t({ en: "Sign in", fr: "Connexion" })}</Link>
            )}
            <Link to={isWaitlist ? "/counselling" : "/book"} onClick={() => setOpen(false)} className="btn-accent text-sm w-full justify-center">{isWaitlist ? t({ en: "Join waitlist", fr: "Liste d'attente" }) : t({ en: "Book a session", fr: "Prendre rendez-vous" })}</Link>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <Footer />
      <Toaster position="top-right" />
    </div>
  );
}

function Footer() {
  const { t } = useLang();
  return (
    <footer className="mt-24 border-t" style={{ background: "var(--bg-secondary)", borderColor: "var(--border-subtle)" }} data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="font-serif text-2xl mb-1" style={{ color: "var(--brand)" }}>JD Drummond</div>
          <div className="overline mb-3">{t({ en: "Counselling & Consulting · Consultation et services-conseils", fr: "Consultation et services-conseils · Counselling & Consulting" })}</div>
          <p className="text-sm max-w-md" style={{ color: "var(--ink-2)" }}>
            {t({
              en: "A private practice offering individual counselling and institutional consulting across Quebec and Ontario.",
              fr: "Un cabinet privé offrant du counseling individuel et des services-conseils institutionnels au Québec et en Ontario."
            })}
          </p>
        </div>
        <div>
          <div className="overline mb-4">{t({ en: "Explore", fr: "Explorer" })}</div>
          <ul className="space-y-2 text-sm" style={{ color: "var(--ink-2)" }}>
            <li><Link to="/services" className="link-underline">{t({ en: "Services", fr: "Services" })}</Link></li>
            <li><Link to="/approach" className="link-underline">{t({ en: "Approach", fr: "Approche" })}</Link></li>
            <li><Link to="/library" className="link-underline">{t({ en: "Client Library", fr: "Bibliothèque client" })}</Link></li>
            <li><Link to="/groups" className="link-underline">{t({ en: "Groups", fr: "Groupes" })}</Link></li>
            <li><Link to="/media" className="link-underline">{t({ en: "Media", fr: "Médias" })}</Link></li>
          </ul>
        </div>
        <div>
          <div className="overline mb-4">{t({ en: "Practice", fr: "Cabinet" })}</div>
          <ul className="space-y-2 text-sm" style={{ color: "var(--ink-2)" }}>
            <li>{t({ en: "Licensed in Quebec & Ontario", fr: "Autorisée au Québec et en Ontario" })}</li>
            <li>{t({ en: "Master of Social Work, McGill", fr: "Maîtrise en travail social, McGill" })}</li>
            <li>{t({ en: "10+ years of practice", fr: "10+ années de pratique" })}</li>
            <li><Link to="/contact" className="link-underline">{t({ en: "Contact", fr: "Contact" })}</Link></li>
          </ul>
          <div className="mt-6 text-xs" style={{ color: "var(--ink-3)" }}>
            <strong>{t({ en: "In crisis?", fr: "En crise?" })}</strong> {t({ en: "Dial 9-8-8 (Canada Suicide Crisis Helpline) or go to your nearest emergency department.", fr: "Composez le 9-8-8 (Ligne d'aide en cas de crise suicidaire du Canada) ou rendez-vous à l'urgence la plus proche." })}
          </div>
        </div>
      </div>
      <div className="border-t py-6 text-center text-xs" style={{ borderColor: "var(--border-subtle)", color: "var(--ink-3)" }}>
        © {new Date().getFullYear()} JD Drummond Counselling &amp; Consulting · Counseling et services-conseils · {t({ en: "All rights reserved", fr: "Tous droits réservés" })}
      </div>
    </footer>
  );
}

function Pill({ children }) {
  return <span className="inline-block text-[11px] px-3 py-1 rounded-full font-mono uppercase tracking-wider" style={{ background: "var(--sage)", color: "var(--brand)" }}>{children}</span>;
}
